import React from 'react';
import { ClockIcon, MailIcon, MapPinIcon, PhoneIcon } from 'lucide-react';
import { Logo } from './Logo';
import { LanguageToggle } from './LanguageToggle';
import { useLanguage } from '../contexts/LanguageContext';
import { business, footerLinks, footerServices } from '../data/business';
import { marketFromHash, requestMarket } from '../utils/market';
import { serviceAreas } from '../data/serviceAreas';
import { ui } from '../data/ui';
export function Footer() {
  const {
    t
  } = useLanguage();
  return <footer className="bg-ink text-white/75" aria-labelledby="footer-heading" data-id="element-137" data-mp="37o56rpk5rbs">
      <h2 id="footer-heading" className="sr-only" data-id="element-138" data-mp="3levih6kghoia">
        Footer
      </h2>
      <div className="container-page grid gap-12 py-16 md:grid-cols-2 lg:grid-cols-12" data-id="element-139" data-mp="3qdf5796vj4bp">
        <div className="lg:col-span-4" data-id="element-140" data-mp="3qf9q4ni5awfs">
          <a href="#top" aria-label={t(ui.home)} className="inline-block" data-id="element-141" data-mp="29yxcxcohlklt">
            <Logo data-id="element-142" data-mp="2ey0907mhoghn" />
          </a>
          <p className="mt-2 text-sm font-semibold text-leaf-light" data-id="element-143" data-mp="2ezba1zojva52">{t(ui.footer.tagline)}</p>
          <p className="mt-4 max-w-sm text-sm leading-relaxed" data-id="element-144" data-mp="3lalh7iesgbvj">{t(ui.footer.about)}</p>
          <div className="mt-6" data-id="element-145" data-mp="3gaz73hu9bbqy">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-white/60" data-id="element-146" data-mp="3gc0ffejw1k4z">{t(ui.footer.languages)}</p>
            <LanguageToggle variant="full" data-id="element-147" data-mp="3v5n74ggw1hz6" />
          </div>
        </div>

        <FooterColumn title={t(ui.footer.quickLinks)} className="lg:col-span-2" data-id="element-148" data-mp="3q724kae5ym7k">
          {footerLinks.map(l => {
          const market = marketFromHash(l.href);
          return <li key={l.href} data-id="element-149" data-mp="335u5kj2dpi2">
                <a href={l.href} onClick={market ? () => requestMarket(market) : undefined} className="hover:text-white" data-id="element-150" data-mp="31umvaqetzm7">
                  {t(l.label)}
                </a>
              </li>;
        })}
        </FooterColumn>

        <div className="lg:col-span-3" data-id="element-151" data-mp="qt015lgpcjue">
          <h3 className="font-display text-sm font-bold uppercase tracking-wider text-white" data-id="element-152" data-mp="qtrpcb7yzfan">{t(ui.footer.services)}</h3>
          <div className="mt-5 grid grid-cols-2 gap-6 lg:grid-cols-1" data-id="element-153" data-mp="mq3fflvv87m8">
            {footerServices.map(group => {
            const market = marketFromHash(group.href);
            return <div key={group.title.en} data-id="element-154" data-mp="yruzmehxdfl5">
                  <p className="text-xs font-semibold uppercase tracking-wider text-leaf-light" data-id="element-155" data-mp="13s9as5etp5ne">{t(group.title)}</p>
                  <ul className="mt-3 space-y-2.5 text-sm" data-id="element-156" data-mp="18ksnl05orwr5">
                    {group.items.map(s => <li key={s.en} data-id="element-157" data-mp="1cl2qu523g92b">
                        <a href={group.href} onClick={market ? () => requestMarket(market) : undefined} className="hover:text-white" data-id="element-158" data-mp="1hkp4ugpqxtc6">
                          {t(s)}
                        </a>
                      </li>)}
                  </ul>
                </div>;
          })}
          </div>
        </div>

        <div className="lg:col-span-3" data-id="element-159" data-mp="3ocgikq153f7b">
          <h3 className="font-display text-sm font-bold uppercase tracking-wider text-white" data-id="element-160" data-mp="3odhwr3dgcht9">{t(ui.footer.contact)}</h3>
          <address className="mt-5 space-y-3 text-sm not-italic" data-id="element-161" data-mp="2aevfvyawhy8f">
            <p className="font-semibold text-white" data-id="element-162" data-mp="2ah84gub5z6ug">{business.name}</p>
            <a href={business.mapsUrl} target="_blank" rel="noopener noreferrer" className="flex items-start gap-2 hover:text-white" data-id="element-163" data-mp="25nlrf11390y4">
              <MapPinIcon className="mt-0.5 h-4 w-4 shrink-0 text-leaf-light" aria-hidden="true" data-id="element-164" data-mp="21kfni01g7lut" />
              {business.addressLine}
            </a>
            <a href={business.phoneHref} className="flex items-center gap-2 hover:text-white" data-id="element-165" data-mp="2tcy80lyqwbag">
              <PhoneIcon className="h-4 w-4 shrink-0 text-leaf-light" aria-hidden="true" data-id="element-166" data-mp="2pdfonumko89q" />
              {business.phoneDisplay}
            </a>
            <a href={`mailto:${business.email}`} className="flex items-center gap-2 hover:text-white" data-id="element-167" data-mp="2kfe4f6deu2wo">
              <MailIcon className="h-4 w-4 shrink-0 text-leaf-light" aria-hidden="true" data-id="element-168" data-mp="2fgkrbf21cdx1" />
              {business.email}
            </a>
            <p className="flex items-center gap-2" data-id="element-169" data-mp="13yiagi5u88q9">
              <ClockIcon className="h-4 w-4 shrink-0 text-leaf-light" aria-hidden="true" data-id="element-170" data-mp="1414i0hsqao8h" />
              {t(ui.footer.hoursShort)}
            </p>
          </address>
          <p className="mt-6 text-sm leading-relaxed text-white/60" data-id="element-171" data-mp="2wnhkfspm1qag">{serviceAreas.map(a => a.city).join(' · ')}, Ohio</p>
        </div>
      </div>

      <div className="border-t border-white/10" data-id="element-172" data-mp="2it87n3qsurxb">
        <div className="container-page flex flex-col gap-2 py-6 text-xs text-white/60 md:flex-row md:items-center md:justify-between" data-id="element-173" data-mp="2nv7amcimnmtq">
          <p data-id="element-174" data-mp="3bhm4hg65ji7a">{t(ui.footer.rights)}</p>
          <p data-id="element-175" data-mp="3fltxvqnk1mbu">
            {business.name} · {business.addressLine} · {business.phoneDisplay}
          </p>
        </div>
      </div>
    </footer>;
}
function FooterColumn({
  title,
  className,
  children
}: {
  title: string;
  className?: string;
  children: React.ReactNode;
}) {
  return <div className={className} data-id="element-176" data-mp="1q6druk4xggq6">
      <h3 className="font-display text-sm font-bold uppercase tracking-wider text-white" data-id="element-177" data-mp="1q5cc42cyv624">{title}</h3>
      <ul className="mt-5 space-y-3 text-sm" data-id="element-178" data-mp="1v6i5m2egaxcp">{children}</ul>
    </div>;
}